import type { JSONDocNode, JSONNode } from '@atlaskit/editor-json-transformer/types';

import { countWordsInAdf } from '../utils/countWords.ts';

// A page must have at least this many words to be a meaningful review target.
const MIN_WORD_COUNT = 500;

// A well-structured page has roughly one heading per this many words. A page with fewer headings
// than that ratio implies is considered to have "low heading density".
const WORDS_PER_HEADING = 300;

// A single plain-text paragraph with at least this many words is considered a "dense" block that
// would benefit from being broken up.
const DENSE_PARAGRAPH_MIN_WORDS = 150;

const MAX_NODES = 50_000;

const countWordsInText = (text?: string): number => text?.match(/\S+/g)?.length ?? 0;

/**
 * Decides whether a just-published page is a good Content Reviewer OnPublish target.
 *
 * The page must have at least {@link MIN_WORD_COUNT} words AND be "under-structured", where
 * under-structured means either:
 *  - **low heading density** — fewer headings than ~1 per {@link WORDS_PER_HEADING} words, or
 *  - **a dense paragraph block** — a plain-text paragraph of at least
 *    {@link DENSE_PARAGRAPH_MIN_WORDS} words.
 *
 * Note: the two under-structured signals are combined with OR (either problem makes the page a
 * candidate). Collapsed `expand`/`nestedExpand` subtrees are ignored so the heading-density ratio is
 * measured against the same content the word count is.
 */
export const isUnderStructuredReviewCandidate = (doc: JSONDocNode | null | undefined): boolean => {
	if (!doc) {
		return false;
	}

	const wordCount = countWordsInAdf(doc);
	if (wordCount < MIN_WORD_COUNT) {
		return false;
	}

	let headingCount = 0;
	let hasDenseParagraph = false;
	let nodeCount = 0;

	const visit = (node: JSONNode): void => {
		nodeCount += 1;
		if (nodeCount > MAX_NODES) {
			return;
		}

		// Ignore collapsed content so structure is measured on the same basis as the word count.
		if (node.type === 'expand' || node.type === 'nestedExpand') {
			return;
		}

		if (node.type === 'heading') {
			headingCount += 1;
		}

		if (node.type === 'paragraph') {
			const children = node.content;
			const isPlainTextOnly =
				!!children && children.length > 0 && children.every((child) => child?.type === 'text');
			if (isPlainTextOnly) {
				const paragraphWordCount = children.reduce(
					(total, child) => total + countWordsInText(child?.text),
					0,
				);
				if (paragraphWordCount >= DENSE_PARAGRAPH_MIN_WORDS) {
					hasDenseParagraph = true;
				}
			}
			// A paragraph only holds inline content, so there is nothing structural to recurse into.
			return;
		}

		const children = node.content;
		if (children) {
			for (const child of children) {
				if (nodeCount > MAX_NODES) {
					break;
				}
				if (child) {
					// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- single pre-order DFS; each ADF node is visited exactly once (O(total nodes)), not quadratic
					visit(child);
				}
			}
		}
	};

	visit(doc);

	const hasLowHeadingDensity = headingCount < wordCount / WORDS_PER_HEADING;

	return hasLowHeadingDensity || hasDenseParagraph;
};
